import {useEffect, useState} from 'react';
import type {MediaRef} from '../store/types';
import {flatArt, panoramaArt} from './art';

/* ===========================================================================
   Pictures.
   ---------------------------------------------------------------------------
   Two kinds of picture live behind one `MediaRef`:

     art   drawn from a seed by `lib/art.ts` — the demo ships zero binaries
     idb   uploaded photos → IndexedDB, NOT localStorage: one 360 photo is
           several megabytes and localStorage would throw. Device-local by
           design — the server stores listing/inquiry/account data, not files.

   `url` stays a valid MediaRef shape (a plain external URL) but nothing in
   this app produces one anymore.

   Callers ask for a `src` string and get one, whichever kind it is.
   =========================================================================== */

const DB_NAME = 'moc-market-media';
const STORE = 'files';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB không mở được'));
  });
}

async function putBlob(id: string, blob: Blob): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(blob, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Không lưu được ảnh'));
  });
  db.close();
}

async function getBlob(id: string): Promise<Blob | null> {
  const db = await openDb();
  const blob = await new Promise<Blob | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(id);
    request.onsuccess = () => resolve((request.result as Blob) ?? null);
    request.onerror = () => reject(request.error ?? new Error('Không đọc được ảnh'));
  });
  db.close();
  return blob;
}

async function deleteBlob(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  db.close();
}

/* --------------------------------------------------------------- resolving */

export function mediaKey(ref: MediaRef): string {
  if (ref.source === 'art') return `art:${ref.scene}:${ref.seed}`;
  if (ref.source === 'idb') return `idb:${ref.id}`;
  return `url:${ref.url}`;
}

/**
 * Resolved URLs are cached forever on purpose.
 *
 * Object URLs are revoked only when the blob is deleted: a gallery that revokes
 * on unmount breaks the browser's own back/forward cache and makes every
 * re-open re-read IndexedDB for a picture that has not changed.
 */
const urlCache = new Map<string, string>();

export async function resolveSrc(ref: MediaRef, kind: 'anh' | '360' = 'anh'): Promise<string> {
  const key = kind === '360' ? `${mediaKey(ref)}#pano` : mediaKey(ref);
  const cached = urlCache.get(key);
  if (cached) return cached;

  let src: string;
  if (ref.source === 'art') {
    src = kind === '360' ? panoramaArt(ref.scene, ref.seed) : flatArt(ref.scene, ref.seed);
  } else if (ref.source === 'url') {
    src = ref.url;
  } else {
    const blob = await getBlob(ref.id);
    src = blob ? URL.createObjectURL(blob) : '';
  }

  urlCache.set(key, src);
  return src;
}

/** `src` for an <img>, resolved asynchronously without flashing the old one. */
export function useMediaSrc(ref: MediaRef | null, kind: 'anh' | '360' = 'anh'): string | null {
  const key = ref ? (kind === '360' ? `${mediaKey(ref)}#pano` : mediaKey(ref)) : '';
  const [src, setSrc] = useState<string | null>(() => (key ? (urlCache.get(key) ?? null) : null));

  useEffect(() => {
    if (!ref) {
      setSrc(null);
      return;
    }
    const ready = urlCache.get(key);
    if (ready) {
      setSrc(ready);
      return;
    }
    let alive = true;
    setSrc(null);

    /* Drawn artwork costs tens of milliseconds of main thread EACH, and a grid
       asks for nine of them at once. Handing that to `setTimeout(0)` fills the
       next half second with long tasks — which is exactly when the shell is
       flying the app in from its icon, and when putting it away again wants a
       rasterised snapshot. So the pictures wait for genuinely idle time and the
       animation gets the frames instead; `timeout` keeps them from waiting
       forever on a busy page. */
    const idle =
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(
            () => {
              void resolveSrc(ref, kind).then((value) => {
                if (alive) setSrc(value);
              });
            },
            {timeout: 900},
          )
        : window.setTimeout(() => {
            void resolveSrc(ref, kind).then((value) => {
              if (alive) setSrc(value);
            });
          }, 0);

    return () => {
      alive = false;
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, [key, kind, ref]);

  return src;
}

/* -------------------------------------------------------------- uploading  */

export interface UploadResult {
  ref: MediaRef;
  width: number;
  height: number;
}

function readSize(file: Blob): Promise<{width: number; height: number}> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({height: image.naturalHeight, width: image.naturalWidth});
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve({height: 0, width: 0});
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });
}

let counter = 0;

export async function putImage(file: File): Promise<UploadResult> {
  const size = await readSize(file);
  counter += 1;
  const id = `m-${Date.now().toString(36)}-${counter.toString(36)}`;
  await putBlob(id, file);
  return {height: size.height, ref: {id, source: 'idb'}, width: size.width};
}

export async function dropImage(ref: MediaRef): Promise<void> {
  if (ref.source !== 'idb') return;
  const key = mediaKey(ref);
  const url = urlCache.get(key);
  if (url) URL.revokeObjectURL(url);
  urlCache.delete(key);
  urlCache.delete(`${key}#pano`);
  await deleteBlob(ref.id);
}

/**
 * A 360 photo is equirectangular: one image covering 360° across and 180° down,
 * which is exactly 2:1. Anything else will look stretched inside the sphere, so
 * the uploader warns instead of silently accepting it.
 */
export function looksEquirectangular(width: number, height: number): boolean {
  if (!width || !height) return true;
  const ratio = width / height;
  return ratio > 1.85 && ratio < 2.15;
}
